/**
 * Where Plaza lives — the PWA side, one direction only.
 *
 * This directory used to hold the entire Playground implementation. It now
 * holds exactly this file: the canonical Plaza host the My Apps preset
 * opens. The implementation lives in `apps/plaza`; nothing here may import
 * from it (see `playgroundBoundary.test.ts`).
 *
 * A hardcoded host would have shipped staging's host in a production build
 * (or the reverse), so an unset var yields `null` and the caller hides the
 * tile rather than offering a link into another environment.
 */

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function configured(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    // Reject anything that is not an absolute http(s) URL, so a malformed
    // var cannot become an open redirect target.
    const url = new URL(trimmed);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return trimTrailingSlash(url.origin);
  } catch {
    return null;
  }
}

/** The canonical Plaza app host, or null when not configured. */
export function playgroundCanonicalUrl(): string | null {
  const origin = configured(import.meta.env.VITE_PLAYGROUND_APP_ORIGIN);
  return origin ? `${origin}/` : null;
}
